import { supabase } from '@/lib/supabase';
import type { MediaItem } from '@/types/db';
import { uploadToCloudinary, isCloudinaryConfigured } from '@/lib/cloudinary';

const BUCKET = 'media';

export async function getMedia(page?: string): Promise<MediaItem[]> {
  let query = supabase.from('media').select('*').order('sort_order', { ascending: true });
  if (page) query = query.eq('page', page);
  const { data, error } = await query;
  if (error) throw error;
  return data ?? [];
}

export async function createMedia(input: Partial<MediaItem>): Promise<MediaItem> {
  const { data, error } = await supabase.from('media').insert(input).select().single();
  if (error) throw error;
  return data;
}

// Insert or update a media row keyed by its unique media_key.
export async function upsertMedia(input: Partial<MediaItem> & { media_key: string }): Promise<MediaItem> {
  const { data, error } = await supabase
    .from('media')
    .upsert(input, { onConflict: 'media_key' })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteMedia(id: string): Promise<void> {
  const { error } = await supabase.from('media').delete().eq('id', id);
  if (error) throw error;
}

// Upload an image and return its public URL.
// Uses Cloudinary when configured; otherwise falls back to the Supabase `media`
// storage bucket.
export async function uploadImage(file: File, path?: string): Promise<string> {
  if (isCloudinaryConfigured()) {
    return uploadToCloudinary(file);
  }
  const ext = file.name.split('.').pop() || 'jpg';
  const key = path || `uploads/${crypto.randomUUID()}.${ext}`;
  const { error } = await supabase.storage.from(BUCKET).upload(key, file, { upsert: true });
  if (error) throw error;
  const { data } = supabase.storage.from(BUCKET).getPublicUrl(key);
  return data.publicUrl;
}
